import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  VerifiedUser as VerifiedIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import PhoneVerification from '../components/PhoneVerification';
import api from '../services/api';

interface VerificationStatus {
  emailVerified: boolean;
  phoneVerified: boolean;
  identityVerified: boolean;
  verificationLevel: number;
  email?: string;
  phoneNumber?: string;
}

export default function VerificationSettings() {
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStatus = async () => {
    try {
      const data = await api.get<VerificationStatus>('/api/registration/verify/status');
      setStatus(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load verification status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const getLevelLabel = (level: number) => {
    switch (level) {
      case 0: return { label: 'Unverified', color: 'error' as const };
      case 1: return { label: 'Email Verified', color: 'warning' as const };
      case 2: return { label: 'Phone Verified', color: 'success' as const };
      case 3: return { label: 'Fully Verified', color: 'success' as const };
      default: return { label: `Level ${level}`, color: 'default' as const };
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const level = status ? getLevelLabel(status.verificationLevel) : getLevelLabel(0);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon color="primary" />
        Account Verification
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Verify your identity to unlock higher transaction limits and enhanced security.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Current Verification Level</Typography>
              <Typography variant="body2" color="text.secondary">
                Higher verification levels unlock more features
              </Typography>
            </Box>
            <Chip
              icon={<VerifiedIcon />}
              label={level.label}
              color={level.color}
              size="medium"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon color={status?.emailVerified ? 'success' : 'disabled'} />
              <Typography variant="body2">
                Email: {status?.emailVerified ? 'Verified' : 'Not Verified'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon color={status?.phoneVerified ? 'success' : 'disabled'} />
              <Typography variant="body2">
                Phone: {status?.phoneVerified ? 'Verified' : 'Not Verified'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VerifiedIcon color={status?.identityVerified ? 'success' : 'disabled'} />
              <Typography variant="body2">
                Identity: {status?.identityVerified ? 'Verified' : 'Not Verified'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {!status?.phoneVerified && (
        <PhoneVerification
          onVerified={fetchStatus}
          initialPhone={status?.phoneNumber}
        />
      )}

      {status?.phoneVerified && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <PhoneIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" color="success.main">Phone Verified</Typography>
            <Typography variant="body2" color="text.secondary">
              {status.phoneNumber || 'Your phone number'} is verified
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
